// ==================================================
// IMPORTS
//
// Purpose:
// React
// Routing
// Components
// Styles
// ==================================================
import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import MessagesIconRail from '../components/messages/MessagesIconRail';
import '../components/messages/MessagesIconRail.css';
import ComposeMessage from '../components/messages/ComposeMessage';
import MessageList from '../components/messages/MessageList';
import MessagePreview from '../components/messages/MessagePreview';
import logo from '../assets/Kuddlecore-Logo.png';
import { useCompactMode } from '../context/CompactModeContext';
import '../styles/Messages.css';
import "../styles/MessagesDndAvailability.css";
import "../styles/MessagesTopActions.css";
import { useNavigate, useLocation } from 'react-router-dom';
import AdvancedSearchPanel from '../components/messages/AdvancedSearchPanel';
import TopMoreMenu from '../components/messages/TopMoreMenu';
import StatusModal from '../components/messages/StatusModal';
import CustomDndModal from '../components/messages/CustomDndModal';
import MessageToolbar from '../components/messages/MessageToolbar';
import NewFolderModal from '../components/messages/NewFolderModal';
import PageQuickNav from "../components/PageQuickNav";
import { useStatusBanner } from "../components/shared/StatusBannerContext";

// ==================================================
// MESSAGE CONFIGURATION
//
// Purpose:
// Default folders
// Default colors
// Default categories
// ==================================================
const defaultFolders = [
  { name: 'Inbox', icon: '📥', color: '#3b82f6' },
  { name: 'Archive', icon: '🗂️', color: '#6b7280' },
  { name: 'Finance', icon: '💰', color: '#10b981' },
  { name: 'Legal', icon: '📄', color: '#f59e0b' },
  { name: 'Custom', icon: '✨', color: '#a855f7' },
];

// ==================================================
// COMPONENT: MESSAGES
//
// Purpose:
// Kuddlecore Messaging Center
//
// Features:
// Inbox
// Compose
// Search
// Availability
// Folders
// ==================================================
const Messages = () => {
  const { compactMode } = useCompactMode();
  const banner = useStatusBanner();

  // ==================================================
  // NAVIGATION & REFS
  //
  // Purpose:
  // Routing
  // Outside click handling
  // Menu references
  // ==================================================
  const navigate = useNavigate();
  const location = useLocation();
  const advancedSearchRef = useRef(null);

  const toolbarMoreRef = useRef(null);
  const preferencesRef = useRef(null);
  const topMoreRef = useRef(null);

  // ==================================================
  // MESSAGE DATA
  //
  // Purpose:
  // Messages
  // Folders
  // Drafts
  // Selection
  // ==================================================
  const [messages, setMessages] = useState([]);
  const [folders, setFolders] = useState(() => {
    const stored = localStorage.getItem('messageFolders');
    return stored ? JSON.parse(stored) : defaultFolders;
  });

  const [activeTab, setActiveTab] = useState('Inbox');
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [selectedMessageIds, setSelectedMessageIds] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCompose, setShowCompose] = useState(false);

  const [composeDraft, setComposeDraft] = useState(() => {
    const stored = localStorage.getItem('composeDraft');
    return stored ? JSON.parse(stored) : null;
  });

  const [filteredTag, setFilteredTag] = useState('');
  const [showNewFolderModal, setShowNewFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderEmoji, setNewFolderEmoji] = useState('😀');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');

  // ==================================================
  // AVAILABILITY & DND
  //
  // Purpose:
  // Focus Mode
  // DND
  // Staff Blocks
  // Saved Schedules
  // ==================================================
  const [currentStatus, setCurrentStatus] = useState(null);
  const [statusText, setStatusText] = useState('');
  const [statusDuration, setStatusDuration] = useState('30 Minutes');
  const [showDndMenu, setShowDndMenu] = useState(false);
  const [showCustomDndModal, setShowCustomDndModal] = useState(false);
  const [moreMenuView, setMoreMenuView] = useState('main');
  const [showDndScheduleModal, setShowDndScheduleModal] = useState(false);
  const [customDndDate, setCustomDndDate] = useState('');
  const [customDndTime, setCustomDndTime] = useState('');
  const [dndStartDate, setDndStartDate] = useState('');
  const [dndEndDate, setDndEndDate] = useState('');
  const [dndRepeat, setDndRepeat] = useState('none');
  const [dndRepeatDays, setDndRepeatDays] = useState([]);
  const [dndVisibleTo, setDndVisibleTo] = useState(['admin', 'director', 'staff']);
  const [dndNotes, setDndNotes] = useState('');
  const [sendInAppNotification, setSendInAppNotification] = useState(true);
  const [sendEmailNotification, setSendEmailNotification] = useState(false);
  const [activeStatuses, setActiveStatuses] = useState([]);

  const [dndScheduleName, setDndScheduleName] = useState('');
  const [savedDndSchedules, setSavedDndSchedules] = useState([]);
  /* === DND Create Schedule Form Fixes === */

  const [dndStartTime, setDndStartTime] = useState('19:00');
  const [dndEndTime, setDndEndTime] = useState('07:00');

  const [showCreateDndSchedule, setShowCreateDndSchedule] = useState(false);
  const [useCenterTimezone, setUseCenterTimezone] = useState(true);

  const [editingDndScheduleId, setEditingDndScheduleId] = useState(null);


  const [dndScheduleAppliesTo, setDndScheduleAppliesTo] = useState('');
  const [customDndDays, setCustomDndDays] = useState([]);

  const [dndBypassRules, setDndBypassRules] = useState({
    emergencyAlerts: true,
    pickupEmergencies: true,
    adminOverride: true,
    incidentReports: true,
  });

  const [savedDraft, setSavedDraft] = useState(() => {
    const stored = localStorage.getItem('composeDraft');
    return stored ? JSON.parse(stored) : null;
  });

  // ==================================================
  // SEARCH & FILTERS
  //
  // Purpose:
  // Search
  // Advanced Search
  // Preferences
  // Sorting
  // ==================================================
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [showMoveMenu, setShowMoveMenu] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);

  const [advancedFilters, setAdvancedFilters] = useState({
    attachment: false,
    photo: false,
    calendar: false,
    date: 'Any Time',
  });

  const [showPreferencesMenu, setShowPreferencesMenu] = useState(false);
  const [messageViewMode, setMessageViewMode] = useState('compact');
  const [sortOrder, setSortOrder] = useState('newest');
  const [showMoreFilters, setShowMoreFilters] = useState(false);

  const [preferenceFilters, setPreferenceFilters] = useState({
    unread: false,
    read: false,
    starred: false,
    calendar: false,
    attachment: false,
    image: false,
  });


  const [advancedSearchFields, setAdvancedSearchFields] = useState({
    from: '',
    to: '',
    subject: '',
    mailbox: 'All Kuddlecore Mailboxes',
  });

  const handleAdvancedSearch = () => {
    const queryParts = [
      advancedSearchFields.from,
      advancedSearchFields.to,
      advancedSearchFields.subject,
    ].filter(Boolean);

    setSearchQuery(queryParts.join(' '));

    if (advancedSearchFields.mailbox === 'All Kuddlecore Mailboxes') {
      setActiveTab('all');
    } else {
      setActiveTab(advancedSearchFields.mailbox.toLowerCase());
    }

    setShowAdvancedSearch(false);
  };

  // ==================================================
  // SEARCH ACTIONS
  //
  // Purpose:
  // Advanced Search
  // Search Reset
  // Search Execution
  // ==================================================
  const handleAdvancedReset = () => {
    setSearchQuery('');

    setAdvancedFilters({
      attachment: false,
      photo: false,
      calendar: false,
      date: 'Any Time',
    });

    setAdvancedSearchFields({
      from: '',
      to: '',
      subject: '',
      mailbox: 'All Kuddlecore Mailboxes',
    });
  };

  // ==================================================
  // DATA LOADING
  //
  // Purpose:
  // Load Messages
  // Load Availability
  // Load Schedules
  // Load Drafts
  // ==================================================
  useEffect(() => {
    if (location.state?.compose) {
      setShowCompose(true);
      setComposeDraft(location.state.draft || null);
    }
  }, [location.state]);

  useEffect(() => {
    localStorage.setItem('messagesWithFolders', JSON.stringify(messages));
  }, [messages]);

  useEffect(() => {
    localStorage.setItem('messageFolders', JSON.stringify(folders));
  }, [folders]);

  useEffect(() => {
    window.openComposeWithDraft = (draft) => {
      setComposeDraft(draft);
      setShowCompose(true);
      setSelectedMessage(null);
    };

    return () => {
      window.openComposeWithDraft = null;
    };
  }, []);

  useEffect(() => {
    const fetchAvailabilityStatus = async () => {
      const localStatus = localStorage.getItem('kuddlecore_active_status');

      if (localStatus) {
        const parsed = JSON.parse(localStatus);

        if (
          parsed.expiresAt &&
          new Date(parsed.expiresAt) > new Date()
        ) {
          setCurrentStatus(parsed);
        } else {
          localStorage.removeItem('kuddlecore_active_status');
        }
      }
      try {
        const res = await fetch('/api/availability', {
          headers: getAuthHeaders(),
        });

        if (!res.ok) return;

        const data = await res.json();

        const statuses = Array.isArray(data) ? data : [data];

        const filteredActiveStatuses = statuses.filter((item) => {
          if (!item?.enabled) return false;

          if (item.expiresAt && new Date(item.expiresAt) <= new Date()) {
            return false;
          }

          return ['dnd', 'focus', 'away'].includes(item.type);
        });

        setActiveStatuses(filteredActiveStatuses);

        if (filteredActiveStatuses.length === 0) {
          setCurrentStatus(null);
          return;
        }

        const activeStatus = filteredActiveStatuses[0];

        localStorage.removeItem('kuddlecore_active_status');

        const untilTime = activeStatus.expiresAt
          ? new Date(activeStatus.expiresAt).toLocaleTimeString([], {
            hour: 'numeric',
            minute: '2-digit',
          })
          : activeStatus.endTime || '';

        const restoredStatus = {
          id: activeStatus._id,
          type: activeStatus.type,
          text:
            activeStatus.text ||
            (activeStatus.type === 'focus'
              ? 'Focus Mode'
              : 'Do Not Disturb'),
          duration: activeStatus.label || activeStatus.duration || '',
          untilTime,
          expiresAt: activeStatus.expiresAt,
          bypassRules: activeStatus.bypassRules || dndBypassRules,
          userName:
            activeStatus.userName ||
            activeStatus.userId?.name ||
            activeStatus.userId?.fullName ||
            'Lynn',
          blockLabel: activeStatus.blockLabel,
          icon: activeStatus.icon,
        };

        setCurrentStatus(restoredStatus);

        localStorage.setItem(
          'kuddlecore_active_status',
          JSON.stringify(restoredStatus)
        );
      } catch (err) {
        console.error('Fetch availability status failed:', err);
      }
    };

    fetchAvailabilityStatus();
    window.addEventListener('kuddle:availability-updated', fetchAvailabilityStatus);

    return () => {
      window.removeEventListener('kuddle:availability-updated', fetchAvailabilityStatus);
    };
  }, []);

  useEffect(() => {
    const fetchFocusSchedules = async () => {
      try {
        const res = await fetch('/api/focus-schedules', {
          headers: getAuthHeaders(),
        });

        if (!res.ok) {
          throw new Error('Failed to fetch focus schedules');
        }

        const data = await res.json();
        const schedules = Array.isArray(data) ? data : [];
        const now = Date.now();

        const enabledSchedules = schedules.filter((item) => {
          if (!item.enabled) return false;

          if (
            item.repeat === 'none' &&
            item.expiresAt &&
            new Date(item.expiresAt).getTime() <= now
          ) {
            return false;
          }

          return true;
        });

        setSavedDndSchedules(enabledSchedules);
      } catch (err) {
        console.error('Fetch focus schedules failed:', err);
      }
    };

    fetchFocusSchedules();

    window.addEventListener('kuddle:focus-schedules-updated', fetchFocusSchedules);

    return () => {
      window.removeEventListener('kuddle:focus-schedules-updated', fetchFocusSchedules);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        advancedSearchRef.current &&
        !advancedSearchRef.current.contains(e.target)
      ) {
        setShowAdvancedSearch(false);
      }

      if (
        toolbarMoreRef.current &&
        !toolbarMoreRef.current.contains(e.target)
      ) {
        setShowMoveMenu(false);
      }

      if (
        preferencesRef.current &&
        !preferencesRef.current.contains(e.target)
      ) {
        setShowPreferencesMenu(false);
      }

      if (
        topMoreRef.current &&
        !topMoreRef.current.contains(e.target)
      ) {
        setShowMoreMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!currentStatus?.expiresAt) return;

    const expiresAt = new Date(currentStatus.expiresAt).getTime();
    const now = Date.now();
    const timeLeft = expiresAt - now;

    if (timeLeft <= 0) {
      setCurrentStatus(null);
      localStorage.removeItem('kuddlecore_active_status');
      return;
    }

    const timer = setTimeout(async () => {
      setCurrentStatus(null);

      setSavedDndSchedules((prev) =>
        prev.filter((schedule) => {
          if (schedule.repeat !== 'none') return true;
          if (!schedule.expiresAt) return true;

          return new Date(schedule.expiresAt).getTime() > Date.now();
        })
      );

      localStorage.removeItem('kuddlecore_active_status');

      try {
        await fetch('/api/focus-schedules/expire', {
          method: 'PATCH',
          headers: getAuthHeaders(),
        });
      } catch (err) {
        console.error('Expire focus schedules failed:', err);
      }

      window.dispatchEvent(new Event('kuddle:focus-schedules-updated'));
      window.dispatchEvent(new Event('kuddle:availability-updated'));
    }, timeLeft);

    return () => clearTimeout(timer);
  }, [currentStatus?.expiresAt]);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (
        showEmojiPicker &&
        !e.target.closest('.emoji-picker-wrapper') &&
        !e.target.closest('.emoji-trigger')
      ) {
        setShowEmojiPicker(false);
      }
    };

    window.addEventListener('click', handleOutsideClick);
    return () => window.removeEventListener('click', handleOutsideClick);
  }, [showEmojiPicker]);

  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const token =
          sessionStorage.getItem('kuddleToken') ||
          localStorage.getItem('kuddleToken');

        const res = await fetch('/api/messages', {
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
          },
        });

        if (!res.ok) throw new Error('Failed to fetch messages');

        const data = await res.json();
        setMessages(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Message fetch failed:', err);
        toast.error('Failed');
      }
    };

    fetchMessages();
  }, []);

  // ==================================================
  // UTILITY FUNCTIONS
  //
  // Purpose:
  // Formatting
  // Display Helpers
  // Labels
  // Time Conversion
  // ==================================================
  const formatScheduleTime = (time) => {
    if (!time) return '';

    const [hour, minute] = time.split(':');

    return new Date(2000, 0, 1, hour, minute).toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const getAuthHeaders = () => {
    const token =
      sessionStorage.getItem('kuddleToken') ||
      localStorage.getItem('kuddleToken');

    return {
      'Content-Type': 'application/json',
      Authorization: token ? `Bearer ${token}` : '',
    };
  };

  const getDndUntilLabel = (duration) => {
    const now = new Date();

    const durationMap = {
      '30 minutes': 30,
      '1 hour': 60,
      '2 hours': 120,
      '4 hours': 240,
      '8 hours': 480,
      '24 hours': 1440,
    };

    const minutes = durationMap[duration] || 30;
    const until = new Date(now.getTime() + minutes * 60000);

    const timeLabel = until.toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
    });

    const isTomorrow = until.getDate() !== now.getDate();

    return timeLabel;
  };

  const formatTypedTime = (value) => {
    let cleaned = value
      .toUpperCase()
      .replace(/[^0-9APM:]/g, '')
      .trim();

    if (!cleaned) return '';

    const hasAM = cleaned.includes('AM');
    const hasPM = cleaned.includes('PM');
    const period = hasAM ? 'AM' : hasPM ? 'PM' : '';

    cleaned = cleaned.replace('AM', '').replace('PM', '');

    let hour = '';
    let minute = '';

    if (cleaned.includes(':')) {
      const parts = cleaned.split(':');
      hour = parts[0] || '';
      minute = parts[1] || '';
    } else {
      if (cleaned.length <= 2) {
        hour = cleaned;
        minute = '00';
      } else {
        hour = cleaned.slice(0, cleaned.length - 2);
        minute = cleaned.slice(-2);
      }
    }

    let hourNumber = Number(hour);
    let minuteNumber = Number(minute);

    if (!hourNumber || hourNumber < 1 || hourNumber > 12) return value;
    if (Number.isNaN(minuteNumber) || minuteNumber < 0 || minuteNumber > 59) {
      return value;
    }

    const finalMinute = String(minuteNumber).padStart(2, '0');
    const finalPeriod = period || 'PM';

    return `${hourNumber}:${finalMinute} ${finalPeriod}`;
  };

  const formatDisplayDate = (dateValue) => {
    if (!dateValue) return '';

    const date = new Date(`${dateValue}T00:00:00`);

    return date.toLocaleDateString([], {
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDisplayTime = (timeValue) => {
    if (!timeValue) return '';

    const [hour, minute] = timeValue.split(':');
    const date = new Date();

    date.setHours(Number(hour));
    date.setMinutes(Number(minute));

    return date.toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const getStatusPillLabel = () => {
    if (!currentStatus) return '';

    if (currentStatus.type === 'focus') {
      const label = currentStatus.untilTime?.split('·')[0]?.trim();

      return label || 'Focus Mode';
    }

    if (currentStatus.duration === 'Specific Duration') {
      return 'Today';
    }

    return currentStatus.untilTime?.includes('Tomorrow')
      ? 'Tomorrow'
      : currentStatus.untilTime || 'Do Not Disturb';
  };

  // ==================================================
  // MESSAGE ACTIONS
  //
  // Purpose:
  // Compose Messages
  // Reply to Messages
  // Filter Messages
  // Open / Close Composer
  // ==================================================
  const openCompose = () => {
    if (savedDraft) {
      const resume = window.confirm('Resume saved draft?');

      if (resume) {
        setComposeDraft(savedDraft);
      } else {
        setComposeDraft(null);
        localStorage.removeItem('composeDraft');
      }
    }

    setSelectedMessage(null);
    setShowCompose(true);
  };

  const closeCompose = () => {
    setShowCompose(false);
    setComposeDraft(null);
  };

  const handleTagFilter = (tag) => {
    setFilteredTag((prev) => (prev === tag ? '' : tag));
  };

  const handleReply = (msg) => {
    const senderName =
      msg.fromUserId?.name || msg.fromUserId?.email || 'Unknown Sender';
    const senderEmail = msg.fromUserId?.email || '';

    toast.info(`✉️ Replying to ${senderName}`);
    banner?.setStatusMessage?.(`Composing reply to ${senderName}`);

    setComposeDraft({
      to: senderEmail,
      subject: msg.subject?.startsWith('Re:')
        ? msg.subject
        : `Re: ${msg.subject}`,
      body: `\n\n--- Original Message ---\nFrom: ${senderName}\nSubject: ${msg.subject}\n\n${msg.body || ''}`,
      tags: msg.tags || [],
    });

    setSelectedMessage(null);
    setShowCompose(true);
  };

  // ==================================================
  // FOLDER ACTIONS
  //
  // Purpose:
  // Create Folder
  // Edit Folder
  // Save Folder
  // ==================================================
  const handleSaveNewFolder = () => {
    if (!newFolderName.trim()) {
      toast.error('Folder name required');
      return;
    }

    if (editingFolder) {
      setFolders((prev) =>
        prev.map((folder) =>
          folder.name === editingFolder.name
            ? { ...folder, name: newFolderName, emoji: newFolderEmoji }
            : folder
        )
      );


      setEditingFolder(null);
    } else {
      setFolders((prev) => [
        ...prev,
        { id: Date.now(), name: newFolderName, emoji: newFolderEmoji },
      ]);
    }

    setShowNewFolderModal(false);
    setNewFolderName('');
    setNewFolderEmoji('😀');
    setShowEmojiPicker(false);
  };

  const handleAddFolderClick = () => {
    setNewFolderName('');
    setNewFolderEmoji('😀');
    setEditingFolder(null);
    setShowNewFolderModal(true);
  };

  // ==================================================
  // MESSAGE FILTERING & SELECTION
  //
  // Purpose:
  // Select Messages
  // Filter Messages
  // Search Results
  // Folder Filtering
  // Tag Filtering
  // ==================================================

  const toggleSelectMessage = (messageId) => {
    setSelectedMessageIds((prev) =>
      prev.includes(messageId)
        ? prev.filter((id) => id !== messageId)
        : [...prev, messageId]
    );
  };

  const filteredMessages = messages.filter((msg) => {
    const senderName =
      msg.fromUserId?.name || msg.fromUserId?.email || 'unknown sender';

    const query = searchQuery.toLowerCase();

    const subjectMatch = msg.subject?.toLowerCase().includes(query);
    const fromMatch = senderName.toLowerCase().includes(query);
    const searchMatch = subjectMatch || fromMatch;

    const tagMatch =
      !filteredTag ||
      msg.tags?.some(
        (tag) => tag.toLowerCase() === filteredTag.toLowerCase()
      );

    let tabMatch = false;

    switch (activeTab.toLowerCase()) {
      case 'all':
        tabMatch = true;
        break;
      case 'unread':
        tabMatch = !msg.read;
        break;
      case 'starred':
        tabMatch = msg.starred;
        break;
      case 'attachments':
        tabMatch = msg.attachments?.length > 0;
        break;
      case 'drafts':
        tabMatch = msg.folder === 'drafts';
        break;
      case 'scheduled':
        tabMatch = msg.folder === 'scheduled';
        break;
      case 'sent':
        tabMatch = msg.folder === 'sent';
        break;
      case 'archive':
        tabMatch = msg.folder === 'archive';
        break;
      case 'spam':
        tabMatch = msg.folder === 'spam';
        break;
      case 'trash':
        tabMatch = msg.folder === 'trash';
        break;
      default:
        tabMatch = msg.folder?.toLowerCase() === activeTab.toLowerCase();
    }

    return tabMatch && tagMatch && searchMatch;
  });

  // ==================================================
  // BULK MESSAGE ACTIONS
  //
  // Purpose:
  // Move
  // Delete
  // Archive
  // Spam
  // Mark Read
  // ==================================================
  const bulkMoveMessages = async (folderName) => {
    if (selectedMessageIds.length === 0) {
      toast.info('Select at least one message first.');
      return;
    }

    try {
      const updatedMessages = await Promise.all(
        selectedMessageIds.map(async (id) => {
          const res = await fetch(`/api/messages/${id}`, {
            method: 'PATCH',
            headers: getAuthHeaders(),
            body: JSON.stringify({ folder: folderName }),
          });

          if (!res.ok) throw new Error('Move failed');

          return res.json();
        })
      );

      setMessages((prev) =>
        prev.map((msg) => {
          const updated = updatedMessages.find((item) => item._id === msg._id);
          return updated || msg;
        })
      );

      setSelectedMessageIds([]);

    } catch (err) {
      console.error('Bulk move failed:', err);
      toast.error('Unable to move');
    }
  };

  const handleBulkArchive = () => bulkMoveMessages('archive');
  const handleBulkSpam = () => bulkMoveMessages('spam');
  const handleBulkDelete = () => bulkMoveMessages('trash');

  const handleBulkMarkRead = async () => {
    if (selectedMessageIds.length === 0) {
      toast.info('Select at least one message.');
      return;
    }

    try {
      const updatedMessages = await Promise.all(
        selectedMessageIds.map(async (id) => {
          const res = await fetch(`/api/messages/${id}`, {
            method: 'PATCH',
            headers: getAuthHeaders(),
            body: JSON.stringify({ read: true }),
          });

          if (!res.ok) throw new Error('Failed to mark as read');

          return res.json();
        })
      );

      setMessages((prev) =>
        prev.map((msg) => {
          const updated = updatedMessages.find((item) => item._id === msg._id);
          return updated || msg;
        })
      );


      setSelectedMessageIds([]);
    } catch (err) {
      console.error('Mark read failed:', err);
      toast.error('Unable to mark as read');
    }
  };

  const handleEmptyTrash = async () => {
    try {
      const res = await fetch('/api/messages/trash/empty', {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      if (!res.ok) throw new Error('Empty trash failed');

      setMessages((prev) => prev.filter((msg) => msg.folder !== 'trash'));

    } catch (err) {
      console.error('Empty trash failed:', err);
      toast.error('Unable to empty trash');
    }
  };

  const handleRestoreTrash = async () => {
    try {
      const res = await fetch('/api/messages/trash/restore', {
        method: 'PATCH',
        headers: getAuthHeaders(),
      });

      if (!res.ok) throw new Error('Restore trash failed');

      const restoredMessages = await res.json();

      setMessages((prev) =>
        prev.map((msg) => {
          const restored = restoredMessages.find((item) => item._id === msg._id);
          return restored || msg;
        })
      );


    } catch (err) {
      console.error('Restore trash failed:', err);
      toast.error('Unable to restore');
    }
  };

  // ==================================================
  // AVAILABILITY ACTIONS
  //
  // Purpose:
  // Quick DND
  // Focus Mode
  // Status Updates
  // Availability Blocks
  // ==================================================
  const handleQuickDnd = async (duration) => {
    const now = new Date();

    const durationMap = {
      '30 minutes': 30,
      '1 hour': 60,
      '2 hours': 120,
      '4 hours': 240,
      '8 hours': 480,
      '24 hours': 1440,
    };

    const minutes = durationMap[duration] || 30;
    const expiresAt = new Date(now.getTime() + minutes * 60000);

    const payload = {
      type: 'dnd',
      enabled: true,
      label: duration,
      text: 'Do Not Disturb',
      source: 'quick',
      startsAt: now.toISOString(),
      expiresAt: expiresAt.toISOString(),
      bypassRules: dndBypassRules,
    };

    try {
      const res = await fetch('/api/availability', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error('Failed to save DND status');
      }

      const savedStatus = await res.json();

      const untilTime = new Date(savedStatus.expiresAt || expiresAt).toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
      });

      const newStatus = {
        id: savedStatus._id,
        type: savedStatus.type || 'dnd',
        text: savedStatus.text || 'Do Not Disturb',
        duration: savedStatus.label || duration,
        untilTime,
        expiresAt: savedStatus.expiresAt || expiresAt.toISOString(),
        bypassRules: savedStatus.bypassRules || dndBypassRules,
      };

      setCurrentStatus(newStatus);

      localStorage.setItem(
        'kuddlecore_active_status',
        JSON.stringify(newStatus)
      );

      setShowMoreMenu(false);
      setMoreMenuView('main');

    } catch (err) {
      console.error(err);
      toast.error('Failed');
    }
  };

  const toggleDndBypassRule = (rule) => {
    setDndBypassRules((prev) => ({
      ...prev,
      [rule]: !prev[rule],
    }));
  };


  // ==================================================
  // RENDER UI
  //
  // Purpose:
  // Messaging Interface
  // Toolbar
  // Preview
  // Compose
  // Modals
  // ==================================================
  return (
    <div className="messages-page-shell">
      <div className={`main-layout ${compactMode ? 'compact-mode' : ''}`}>
        <MessagesIconRail
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          onCompose={openCompose}
          onAddFolder={handleAddFolderClick}
          onEmptyTrash={handleEmptyTrash}
          onRestoreTrash={handleRestoreTrash}
        />

        <div className="messages-page">
          <main className="messages-main-view">
            <div className="messages-header">
              <div className="messages-brand-left">
                <img
                  src={logo}
                  alt="Kuddlecore"
                  className="messages-header-logo"
                />
              </div>

              <div
                className="message-search-shell header-search-shell"
                ref={advancedSearchRef}
              >
                <input
                  type="text"
                  className="message-search-input"
                  placeholder="Search messages..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />

                <button
                  type="button"
                  className="advanced-search-btn"
                  title="Advanced search"
                  onClick={() => setShowAdvancedSearch((prev) => !prev)}
                >
                  ⚙️
                </button>

                <button
                  type="button"
                  className="message-search-btn"
                  title="Search"
                  onClick={() => {
                    setShowAdvancedSearch(false);
                    const input = document.querySelector('.message-search-input');
                    if (input) input.focus();
                  }}
                >
                  🔍
                </button>

                {showAdvancedSearch && (
                  <AdvancedSearchPanel
                    advancedSearchFields={advancedSearchFields}
                    setAdvancedSearchFields={setAdvancedSearchFields}
                    advancedFilters={advancedFilters}
                    setAdvancedFilters={setAdvancedFilters}
                    handleAdvancedReset={handleAdvancedReset}
                    handleAdvancedSearch={handleAdvancedSearch}
                  />
                )}

              </div>

              <div className="messages-top-actions">

                <button type="button" onClick={() => setActiveTab('starred')}>
                  ⭐ Starred
                </button>

                <button type="button" onClick={() => setActiveTab('people')}>
                  👤 Contacts
                </button>

                <div className="top-more-shell" ref={topMoreRef}>
                  <button
                    type="button"
                    className={
                      currentStatus?.type === 'dnd' || currentStatus?.type === 'focus'
                        ? 'top-more-status-btn'
                        : 'top-more-btn'
                    }
                    aria-label={
                      currentStatus?.type === 'focus'
                        ? 'Focus mode active'
                        : currentStatus?.type === 'dnd'
                          ? 'Do Not Disturb active'
                          : 'More options'
                    }
                    onClick={() => {
                      setShowMoreMenu((prev) => !prev);
                      setMoreMenuView('main');
                    }}
                  >
                    {activeStatuses.length > 0
                      ? `${activeStatuses.length} Active Blocks ▾`
                      : 'More ▾'}
                  </button>
                  {showMoreMenu && (
                    <TopMoreMenu
                      activeStatuses={activeStatuses}
                      moreMenuView={moreMenuView}
                      setMoreMenuView={setMoreMenuView}
                      currentStatus={currentStatus}
                      navigate={navigate}
                      setSelectedStatus={setSelectedStatus}
                      setStatusText={setStatusText}
                      setShowStatusModal={setShowStatusModal}
                      setShowMoreMenu={setShowMoreMenu}
                      setShowCustomDndModal={setShowCustomDndModal}
                      setShowDndScheduleModal={setShowDndScheduleModal}
                      handleQuickDnd={handleQuickDnd}
                      dndBypassRules={dndBypassRules}
                      toggleDndBypassRule={toggleDndBypassRule}
                      savedDndSchedules={savedDndSchedules}
                      setSavedDndSchedules={setSavedDndSchedules}
                      setCurrentStatus={setCurrentStatus}
                      getAuthHeaders={getAuthHeaders}
                    />
                  )}
                </div>

                <PageQuickNav showHome />
              </div>
            </div>


            {selectedMessage ? (
              <MessagePreview
                message={selectedMessage}
                onClose={() => setSelectedMessage(null)}
                highlightQuery={searchQuery}
                onReply={handleReply}
                folders={folders}
                onMoveFolder={async (message, folderName) => {
                  const normalizedFolder = folderName.toLowerCase();

                  try {
                    const res = await fetch(`/api/messages/${message._id}`, {
                      method: 'PATCH',
                      headers: getAuthHeaders(),
                      body: JSON.stringify({ folder: normalizedFolder }),
                    });

                    if (!res.ok) throw new Error('Move failed');

                    const updated = await res.json();

                    setMessages((prev) =>
                      prev.map((item) =>
                        item._id === updated._id ? updated : item
                      )
                    );

                    setSelectedMessage(updated);
                  } catch (err) {
                    console.error('Move message failed:', err);
                    toast.error('Unable to move');
                  }
                }}
              />
            ) : (
              <>
                <MessageToolbar
                  selectedMessageIds={selectedMessageIds}
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  preferencesRef={preferencesRef}
                  showPreferencesMenu={showPreferencesMenu}
                  setShowPreferencesMenu={setShowPreferencesMenu}
                  messageViewMode={messageViewMode}
                  setMessageViewMode={setMessageViewMode}
                  sortOrder={sortOrder}
                  setSortOrder={setSortOrder}
                  preferenceFilters={preferenceFilters}
                  setPreferenceFilters={setPreferenceFilters}
                  showMoreFilters={showMoreFilters}
                  setShowMoreFilters={setShowMoreFilters}
                  filteredMessages={filteredMessages}
                  setSelectedMessageIds={setSelectedMessageIds}
                  handleBulkArchive={handleBulkArchive}
                  handleBulkDelete={handleBulkDelete}
                  handleBulkMarkRead={handleBulkMarkRead}
                  handleBulkSpam={handleBulkSpam}
                  toolbarMoreRef={toolbarMoreRef}
                  showMoveMenu={showMoveMenu}
                  setShowMoveMenu={setShowMoveMenu}
                />

                <MessageList
                  messages={filteredMessages}
                  selectedFolder={activeTab}
                  onPreview={setSelectedMessage}
                  onTagFilter={handleTagFilter}
                  compact={compactMode}
                  searchQuery={searchQuery}
                  folders={folders}
                  selectedMessageIds={selectedMessageIds}
                  toggleSelectMessage={toggleSelectMessage}
                  onMoveMessage={(msgId, newFolder) => {
                    setMessages((prev) =>
                      prev.map((item) =>
                        item._id === msgId
                          ? { ...item, folder: newFolder }
                          : item
                      )
                    );
                  }}
                />
              </>
            )}

            {showCompose && (
              <div className="compose-floating-container">
                <ComposeMessage
                  folders={folders}
                  initialTo={composeDraft?.to || ''}
                  initialSubject={composeDraft?.subject || ''}
                  initialBody={composeDraft?.body || ''}
                  initialTags={composeDraft?.tags || []}
                  onSend={async (messageData) => {
                    try {
                      const currentUser =
                        JSON.parse(localStorage.getItem('kuddleUser')) || {};

                      const newMessage = {
                        fromUserId: currentUser._id,
                        toUserId: null,
                        audienceType: 'staff',
                        subject: messageData.subject,
                        body: messageData.body,
                        folder: (messageData.folder || 'inbox').toLowerCase(),
                        tags: messageData.tags || [],
                        read: false,
                        starred: false,
                        isReminder: false,
                      };

                      const res = await fetch('/api/messages', {
                        method: 'POST',
                        headers: getAuthHeaders(),
                        body: JSON.stringify(newMessage),
                      });

                      if (!res.ok) throw new Error('Message send failed');

                      const savedMessage = await res.json();

                      setMessages((prev) => [savedMessage, ...prev]);
                      toast.success('Sent');

                      setShowCompose(false);
                      setComposeDraft(null);
                    } catch (err) {
                      console.error('Message send failed:', err);
                      toast.error('Failed');
                    }
                  }}
                  onClose={closeCompose}
                />
              </div>
            )}
          </main>
        </div>

        <NewFolderModal
          showNewFolderModal={showNewFolderModal}
          setShowNewFolderModal={setShowNewFolderModal}
          editingFolder={editingFolder}
          newFolderName={newFolderName}
          setNewFolderName={setNewFolderName}
          newFolderEmoji={newFolderEmoji}
          setNewFolderEmoji={setNewFolderEmoji}
          showEmojiPicker={showEmojiPicker}
          setShowEmojiPicker={setShowEmojiPicker}
          handleSaveNewFolder={handleSaveNewFolder}
        />

        <StatusModal
          showStatusModal={showStatusModal}
          setShowStatusModal={setShowStatusModal}
          selectedStatus={selectedStatus}
          statusText={statusText}
          setStatusText={setStatusText}
          statusDuration={statusDuration}
          setStatusDuration={setStatusDuration}
          setCurrentStatus={setCurrentStatus}
        />

        <CustomDndModal
          showCustomDndModal={showCustomDndModal}
          setShowCustomDndModal={setShowCustomDndModal}
          customDndDate={customDndDate}
          setCustomDndDate={setCustomDndDate}
          customDndTime={customDndTime}
          setCustomDndTime={setCustomDndTime}
          setCurrentStatus={setCurrentStatus}
          formatDisplayDate={formatDisplayDate}
          formatDisplayTime={formatDisplayTime}
          dndBypassRules={dndBypassRules}
          getAuthHeaders={getAuthHeaders}
        />
      </div>
    </div>
  );
};

export default Messages;